import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { fetchAuditedTableData } from '../../../../../../store/company/action';
import { getTimeStamp } from '../../../../../../utils/dateUtils';
import { TENURE } from '../../../../../../store/dashboard/auditedFinancials/constants';

interface UseProvisionalStatementDataParams {
    orgId: string;
    /** The backend `misDisplayName` this instance owns (e.g. 'Balance Sheet'). Empty until resolved. */
    metric: string;
    /** Other MIS only: fetched through the raw-classification path rather than the statement path. */
    raw?: boolean;
    /** Other MIS only: the classification whose raw rows to pull ('1' / Unclassified). */
    classificationId?: string;
}

/**
 * Owns the fetch for ONE provisional statement, so the stacked page (RECUR-27640) can run an
 * instance per section — the same shape as `useAuditedStatementData`, which exists for exactly
 * this reason on the audited side.
 *
 * Differs from the audited hook in three ways, all backend-driven:
 *   - `type: 'provisionalstatements'` rather than 'auditedfinancials'
 *   - always sends `tenure: YEARLY` (the provisional endpoint requires it)
 *   - supports the raw path (`raw: true` + `classificationId`), which is how Other MIS has
 *     always been loaded — see the metric effect this replaced.
 *
 * No polling and no nature/consolidation switch: neither exists for provisional data.
 */
export function useProvisionalStatementData({ orgId, metric, raw = false, classificationId }: UseProvisionalStatementDataParams) {
    const dispatch = useDispatch();

    // Identity of the data currently held. Visible state is DERIVED from it rather than mutated by
    // the effect, so there is no render — however brief — where a previous org's figures show under
    // the new org. An effect runs after commit, so a mutate-on-change approach always paints stale
    // data once, which on a financials surface is not acceptable.
    const requestKey = `${orgId}::${metric}::${raw ? `raw:${classificationId ?? ''}` : 'joined'}`;
    const [held, setHeld] = useState<{ key: string; data: any }>({ key: '', data: {} });
    const requestRef = useRef(0);

    const isCurrent = held.key === requestKey;
    const tableData = isCurrent ? held.data : {};
    // Nothing to fetch → settled and empty. Otherwise we are loading until this exact request's
    // response is the one being held.
    const loading = !metric || !orgId ? false : !isCurrent;

    const fetchTableData = useCallback(() => {
        if (!metric || !orgId) return;
        const requestId = ++requestRef.current;
        const isStale = () => requestId !== requestRef.current;
        dispatch(
            fetchAuditedTableData(
                {
                    type: 'provisionalstatements',
                    mis: metric,
                    orgId,
                    requestId: getTimeStamp(),
                    raw,
                    tenure: TENURE.YEARLY,
                    ...(raw && classificationId ? { classificationId } : {}),
                },
                {
                    // Mark the request settled even on failure, so `loading` cannot pin forever.
                    onError: () => {
                        if (isStale()) return;
                        setHeld({ key: requestKey, data: {} });
                    },
                    onSuccess: (data: any) => {
                        if (isStale()) return;
                        setHeld({ key: requestKey, data });
                    },
                },
            ),
        );
    }, [dispatch, metric, orgId, raw, classificationId, requestKey]);

    useEffect(() => {
        if (!metric || !orgId) {
            // Bump the generation so an in-flight response cannot restore rows we are done with.
            requestRef.current += 1;
            return;
        }
        fetchTableData();
    }, [fetchTableData, metric, orgId]);

    return { tableData, loading, refetch: fetchTableData };
}
